import { ExternalAgentIdentifier, YapMoney } from './general';
import { PhoneNumber, PostalAddress } from '@/types/address';
import { VirtualIdentity } from '@/types/virtualIdentities';
import { Subscription } from '@/components/billing/types/subscription';
import { Delegate } from '@/components/cards/types/delegate';
import {
  EntitlementChangeOrderInfo,
  FunctionChangeInfo,
  PartnershipPersonEntitlements,
  RoleChangeInfo
} from '@/types/entitlement';

export enum PartnershipURI {
  Base = '/partnerships',
  BaseV2 = '/v2/partnerships',
  Extension = '/extension/partnerships',

  Activities = '/activities',
  Agent = '/agent',
  Alerts = '/alerts',
  Candidates = '/partners/candidates',
  CashAccounts = '/cash-accounts',
  CashAccountsUnfilitered = '/cash-accounts/unfiltered',
  CardPrograms = '/card-programs',
  CardProgramsOrder = `/card-programs/order`,
  ComplianceDeclaration = '/compliance-declaration',
  Confirmations = '/confirmations',
  Confirmation = '/confirmation',
  ControlPersons = '/control-persons',
  DeclarationsList = '/declarations/list',
  Delegate = '/delegate',
  DeputyChange = '/deputy-change',
  DomicileAddress = '/domicile-address',
  EbillSubscriptions = '/ebill-subscriptions',
  /**
   * @deprecated Use /v2/agents/{uid}/cards instead
   */
  MyCards = '/my-cards',
  MyEntitlements = '/my-entitlements',
  Impact = '/impact',
  Invoices = '/invoices',
  Members = '/members',
  Name = '/name',
  Objectives = '/objectives',
  Partner = '/partner',
  PendingPaymentOrders = '/pending-payment-orders',
  RelatedBillers = '/related-billers',
  Requestor = '/requestor',
  Revoke = '/revoke',
  Roles = '/roles',
  VirtualIdentities = '/virtual-identities'
}

export enum ServiceObjective {
  Deepbox = 'deepbox',
  DomesticAccount = 'domestic-account',
  Debitcard = 'debitcard',
  DebitcardProgram = 'debitcard-program',
  EbillBusinessAccess = 'ebill-business-access'
}

export interface PartnershipServiceObjective {
  refDataTag: string;
  title: string;
  hint: string;
  service: ServiceObjective;
  reached: boolean;
  reachedAt: string;
}

export interface PartnershipObjective {
  uid: string;
  createdAt: string;
  updatedAt: string;
  status: 'active';
  data: {
    agentUid: string;
    serviceObjectives: PartnershipServiceObjective[];
    changeLogData: {
      changedByAgentUid: string;
      changedAt: string;
    }[];
  };
}

export enum Empowerment {
  None = 'none',
  AsOne = 'as_one',
  AsTwo = 'as_two',
  AsThree = 'as_three',
  AsProportional = 'vote'
}

enum PartnershipEmpowerment {
  AsOne = 'as_one',
  AsTwo = 'as_two',
  AsThree = 'as_three',
  None = 'none'
}

export enum LegalFormNonRegistered {
  Abstract = 'abstract',
  Association = 'association',
  BodyCorporate = 'body_corporate',
  Simple = 'simple_partnership'
}

export enum PartnershipLegalForm {
  AbstractPartnership = 'abstract_partnership',
  Association = 'association',
  BodyCorporate = 'body_corporate',
  CompanyLimitedByShares = 'company_limited_by_shares',
  Cooperative = 'cooperative',
  Foundation = 'foundation',
  GeneralPartnership = 'general_partnership',
  Institute = 'institute',
  LimitedLiabilityCompany = 'limited_liability_company',
  LimitedPartnership = 'limited_partnership',
  OwnershipInUndividedShares = 'ownership_in_undivided_shares',
  PartnershipLimitedByShares = 'partnership_limited_by_shares',
  PublicCooperative = 'public_cooperative',
  PublicCorporationLimitedByShares = 'public_corporation_limited_by_shares',
  PublicInstitution = 'public_institution',
  RegisteredCouple = 'registered_couple',
  SimplePartnership = 'simple_partnership',
  SoleProprietorship = 'sole_proprietorship',
  SpecialLegalForm = 'special_legal_form'
}

export enum Role {
  Bootstrapper = 'bootstrapper',
  Partner = 'partner',
  Delegate = 'delegate',
  Deputy = 'deputy',
  Requestor = 'requestor'
}

enum RolesToVote {
  PartnerOrDeputy = 'partnerOrDeputy',
  Delegate = 'delegate',
  PartnerOrDelegateOrDeputy = 'partnerOrDelegateOrDeputy',
  Partner = 'partner'
}

export enum PartnershipStatus {
  New = 'new',
  Registered = 'registered',
  Screened = 'screened',
  Identified = 'identified',
  Bankable = 'bankable',
  Passive = 'passive',
  Cancelling = 'cancelling',
  Cancelled = 'cancelled'
}

enum ConstitutionStatus {
  Bankable = 'bankable',
  Cancelling = 'cancelling',
  Confirmed = 'confirmed',
  Constituted = 'constituted',
  Funded = 'funded',
  Identified = 'identified',
  Processing = 'processing'
}

export enum DeepSignStatus {
  New = 'new',
  Draft = 'draft',
  InProgress = 'in-progress',
  Signed = 'signed',
  Withdrawn = 'withdrawn'
}

export interface Partnership {
  uid: string;
  updatedAt: string;
  status: PartnershipStatus;
  onboardingData: {
    RegistrationAgentUid?: string;
    identificationOnly?: boolean;
    isPassported?: boolean;
    isQualified?: boolean;
    isRegistered?: boolean;
  };
  name: {
    name: string;
  };
  entity: {
    defaultEmpowerment: Empowerment;
    hasMandates: boolean;
    legalForm: PartnershipLegalForm;
    numPartners: number;
    purpose: string;
    registered: boolean;
    registryNogaCodes: string[];
    votingWeight: number;
  };
  externalIds?: ExternalAgentIdentifier[];
  decoration: {
    emailAddress: string;
    logoFileName: string;
    phoneNumber: PhoneNumber;
    webPageUrl: string;
  };
  createdAt: string;
  contactDetails?: {
    domicileAddress: PostalAddress;
    emailAddress: string;
    phoneNumber: PhoneNumber;
  };
  constitution?: {
    constitutionCapital: YapMoney;
    constitutionConfirmation: string;
    constitutionDeepbox: {
      deepboxNodeId: string;
      deepsignDocId: string;
      deepsignSignatures: {
        email: string;
        signedAt: string;
        signeeId: string;
      }[];
      deepsignStatus: DeepSignStatus;
      isSent: boolean;
      isSigned: boolean;
      languageCode: string;
    };
    constitutionEvidence: string;
    constitutionFee: YapMoney;
    constitutionStatus: ConstitutionStatus;
    identifiedPartnershipUid: string;
    registryInformation: {
      identified: ExternalAgentIdentifier;
      registryEntry: {
        address: PostalAddress;
        legalForm: PartnershipLegalForm.CompanyLimitedByShares | PartnershipLegalForm.LimitedLiabilityCompany;
        name: string;
      };
    };
    signedConstitutionConfirmation: string;
  };
  objectives?: PartnershipServiceObjective[];
  requestorDetails?: {
    ecoPartnerName: string;
    ecoPartnerUid: string;
    personName: string;
    personUid: string;
    partnershipName: string;
    partnershipUid: string;
  };
  context?: {
    identificationContextUid?: string;
    partnershipRegistrationOrderUid: string;
    partnershipCharterUid: string;
    constitutionPartnershipUid?: string;
    contractRequestOrderUID?: string;
  };
  virtualIdentities?: VirtualIdentity[];
  subscription?: Subscription;
  delegates?: Delegate[];
  partners?: Delegate[];
  partnerEngagements: PartnerEngagement[];
  deputies?: Delegate[];
}

export interface PartnershipProfile {
  kyc: {
    result: string;
    lastCheck: string;
    verificationResult: string;
  };
  aml?: {
    riskCategory: string;
    lastCheck: string;
    currentRiskAssessment: {
      assignmentMode: string;
      assignedRiskCategory: string;
      assignedRiskRecordDate: string;
      riskAssessmentCertificateUID: string;
    };
  };
  amlCategory: {
    categoryId: string;
  };
  policy: {
    safetyPolicy: string;
    entitlementPolicy: string;
  };
  complianceDeclaration: Record<string, any>;
  complianceDeclarationApproval: {
    status: string;
    weight: number;
    threshold: number;
    approvals?: ApprovalByRoleData[];
    failOnReject: boolean;
    rolesToVote: RolesToVote;
  };
  _links: {
    self: {
      uri: string;
    };
  };
}

export interface PartnershipRole {
  personUid: string;
  processingStatus: {
    status: string;
    verifications: {
      domicile: string;
      domicile_matched: string;
      empowerment: string;
      identity: string;
      'is-declared': string;
      'is-registered': string;
      'is-synthetic': string;
      name_matched: string;
      payment: string;
      screening: string;
    };
  };
  name: string;
  requestedName: string;
  empowerment: Empowerment;
  role: Role;
  status: string;
  weight: number;
  threshold: number;
  matchingName: boolean;
  matchingDomicile: boolean;
  matchingMandate: {
    mandatee: {
      personIds: ExternalAgentIdentifier[];
      firstname: string;
      lastname: string;
      domicile: PostalAddress;
      gender: string;
      hometown: string;
    };
    empowerment: Empowerment;
  };
  requestedAsDelegate: boolean;
  extensionOrderUid: string;
  agentRoleInContractUid: string;
}

export enum PartnershipEngagementStatus {
  New = 'new',
  Active = 'active'
}

export enum PartnershipEngagementRequestStatus {
  New = 'new',
  Accepted = 'accepted',
  Cancelled = 'cancelled',
  Executed = 'executed',
  Expired = 'expired',
  Rejected = 'rejected',
  Retired = 'retired'
}

export interface PartnershipEngagement {
  uid: string;
  status: PartnershipStatus;
  requestStatus: PartnershipEngagementRequestStatus;
  name: string;
  legalForm: PartnershipLegalForm;
  objectives?: PartnershipServiceObjective[];
  virtualIdentities?: VirtualIdentity[];
  subscription?: Subscription;
  deputies?: Delegate[];
  delegates?: Delegate[];
  partners?: Delegate[];
  partnerEngagements: PartnerEngagement[];
}

export interface PartnerEngagement {
  uid: string;
  name: string;
  engagementStatus: PartnershipEngagementStatus;
  roles: Role[];
  empowerment: Empowerment;
}

export interface Agreement {
  uri?: string;
  link?: string;
  type?: string;
  title?: string;
  group?: string;
  optional: boolean;
  parent?: string;
  content?: string;
  actionText: string;
}

export interface DataSharing {
  contractUid: string;
  ecoPartnerName: string;
}

export interface PartnershipAlert {
  alertUid: string;
  message: string;
  reasonCode: string;
  status: PartnershipAlertStatus;
  trigger: {
    entityRevNr: number;
    entityStatus: string;
    entityType: string;
    entityUid: string;
  };
  triggeredAt: string;
}

enum PartnershipAlertStatus {
  Cancelled = 'cancelled',
  Cancelling = 'cancelling',
  New = 'new',
  Off = 'off',
  On = 'on'
}

export interface PartnershipMember {
  delegateRoleUid: string;
  functions?: 'all_rights'[];
  personInfo: {
    name: string;
    onboardingDetails: {
      hasBiometricIdentity?: boolean;
      hasLimitedIdentity?: boolean;
      isDeclared?: boolean;
      isIdentificationOnly?: boolean;
      isPassported?: boolean;
      isQualified?: boolean;
      isRegistered?: boolean;
    };
    status: string;
    uid: string;
  };
  roleTypes: Role[];
}

export interface PartnershipMemberDetails {
  entitlements?: PartnershipPersonEntitlements;
  pendingEntitlements?: EntitlementChangeOrderInfo[];
  pendingFunctionChanges?: FunctionChangeInfo[];
  pendingRoleChanges?: RoleChangeInfo[];
  roleTypes?: Role[];
}

enum PartnershipCandidateStatus {
  Matched = 'matched',
  MatchingRequestor = 'matchingRequestor',
  Nominated = 'nominated'
}

export interface PartnershipSignatoryCandidate {
  partner: {
    empowerment: Empowerment;
    mandatee: {
      personIds: ExternalAgentIdentifier[];
      firstname: string;
      lastname: string;
      domicile: PostalAddress;
      gender: string;
      hometown: string;
    };
  };
  status: PartnershipCandidateStatus;
}

export interface PartnershipDeputyChange {
  agentsToAddAllRights: string[]; // Array of UUIDs for agents to add all rights
  agentsToRemoveAllRights: string[]; // Array of UUIDs for agents to remove all rights
  changeData?: ChangeData; // Data related to the change
  deputiesToAdd: string[]; // Array of UUIDs for agents to become deputies
  deputiesToRemove: string[]; // Array of UUIDs for agents to remove deputies
}

interface ChangeData {
  approvalData?: PartnershipApprovalData; // Approval record
  changeStatus?: 'approve' | 'done' | 'edit'; // Status of the change
  changedAt?: string; // Time of change in ISO 8601 format
  changedByPersonUid?: string; // UUID of the person who made the change
  changedFrom?: 'approve' | 'done' | 'edit'; // Previous change status
  message?: string; // Message for the partnership
}

interface PartnershipApprovalData {
  approvals?: ApprovalByRoleData[]; // Array of approval records
  failOnReject: boolean; // Indicates how to react on fails
  rolesToVote: RolesToVote; // Role to vote
  status: string; // Status of approval (e.g., "approved")
  threshold: number; // Threshold for voting
  weight: number; // Weight of approvals so far
}

interface ApprovalByRoleData {
  agentRoleInContractUid: string; // Approving role (UUID pattern)
  agentUid?: string; // Approving agent (UUID pattern)
  approvalUid?: string; // Externalized approval UID (UUID pattern)
  lastUpdatedAt: string; // Last update in ISO 8601 format
  status: string; // Approval status
}
